import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { communityList, communityTags } from "@/mocks/community";
import CategoryHero from "@/components/feature/CategoryHero";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 6;

export default function CommunityList() {
  useDocumentMeta(
    "互动社区 - 色花堂",
    "色花堂互动社区，浏览大家发表的娱乐相关帖子，参与影视评论、明星八卦与活动交流，找到志同道合的伙伴。",
  );
  const [tag, setTag] = useState<string>("全部");
  const [page, setPage] = useState(1);

  const handleTag = (next: string) => {
    setTag(next);
    setPage(1);
  };

  const filtered = useMemo(() => {
    const list = tag === "全部" ? communityList : communityList.filter((post) => post.tag === tag);
    return [...list].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [tag]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div>
      <CategoryHero
        title="互动社区"
        description="在这里浏览大家发表的娱乐相关帖子，参与讨论、分享观点，找到志同道合的伙伴。"
        icon="ri-chat-smile-2-line"
        crumbs={[{ label: "首页", to: "/" }, { label: "社区" }]}
      />

      <div className="px-4 py-8 md:px-8">
        <div className="mx-auto w-full max-w-[1280px]">
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex flex-wrap items-center gap-1 rounded-full bg-background-100 p-1">
              {["全部", ...communityTags].map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => handleTag(item)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors cursor-pointer ${
                    tag === item
                      ? "bg-primary-500 font-semibold text-background-50"
                      : "text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          <p className="mt-4 text-sm text-foreground-500">
            当前板块共 <span className="font-semibold text-primary-700">{filtered.length}</span> 篇帖子
          </p>

          {pageItems.length > 0 ? (
            <>
              <div className="mt-4 flex flex-col gap-3">
                {pageItems.map((post) => (
                  <Link
                    key={post.id}
                    to={`/community/detail/${post.id}`}
                    className="group flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:flex-row md:items-center md:gap-5 md:p-5 cursor-pointer"
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                      <i className="ri-user-3-line text-xl" aria-hidden="true"></i>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-800">
                          {post.tag}
                        </span>
                        <h2 className="font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
                          {post.title}
                        </h2>
                      </span>
                      <span className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line" aria-hidden="true"></i>
                          {post.author}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line" aria-hidden="true"></i>
                          {post.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line" aria-hidden="true"></i>
                          {post.views.toLocaleString()}
                        </span>
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-4 text-sm text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-chat-3-line text-base" aria-hidden="true"></i>
                        {post.replyList.length}
                      </span>
                      <i
                        className="ri-arrow-right-s-line text-xl text-foreground-300 transition-colors group-hover:text-primary-600"
                        aria-hidden="true"
                      ></i>
                    </span>
                  </Link>
                ))}
              </div>
              <Pagination current={page} total={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="mt-4">
              <EmptyState
                icon="ri-chat-off-line"
                title="该板块暂时还没有内容"
                description="换一个热门话题标签看看，或者先浏览其他板块的精彩讨论。"
                actionLabel="查看全部帖子"
                onAction={() => handleTag("全部")}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}